/**
 * SPEC.md §10 — M6.8 (issue #35): the command palette's data layer.
 * `buildCommands` is a pure function of everything the palette can act on —
 * it returns `Command[]`, each just a label plus a `run()` thunk that calls
 * the *exact same* underlying function its menu equivalent already calls:
 * `export.ts`'s `saveSvg`/`saveRaster`/the `toXxx` Copy-as generators
 * (ExportMenu.tsx/CopyMenu.tsx's own imports), `interfaceTheme.ts`'s
 * `toggleInterfaceTheme` (InterfaceThemeToggle.tsx's own handler), and the
 * callbacks App.tsx already threads to Topbar (`onSelectTemplate`,
 * `onDiagramThemeChange`, `onLookChange`, `onClearAllData`). No parallel
 * implementation of any action, per the ticket's own AC.
 *
 * `filterCommands` is the palette's entire search model: a case-insensitive
 * substring match against each command's label, kept here (not inline in
 * CommandPalette.tsx) so it's unit-testable without React or a DOM.
 *
 * `destructive: true` on a command (only `clear-all-local-data` carries it)
 * tells `CommandPalette.tsx` to route the selection through an inline
 * confirm step instead of calling `run()` immediately. This matters because
 * `onClearAllData` (App.tsx's `handleClearAllData`) is the wipe itself, with
 * no gate of its own — `AboutMenu.tsx`'s "requires a confirmation step
 * before anything is deleted" (M6.5, issue #32) is UI state that component
 * owns, not part of the shared callback. Calling `onClearAllData` straight
 * from a palette selection would reuse the wipe but silently drop the
 * safety gate around it — not "the same underlying action," the same
 * destructive half of it with the confirmation missing.
 */
import { DIAGRAM_TYPES, type DiagramType } from './diagramTypes';
import {
  RASTER_FORMAT_META,
  RASTER_FORMATS,
  SCALE_OPTIONS,
  saveRaster,
  saveSvg,
  toDataUri,
  toHtmlEmbed,
  toInlineSvg,
  toMarkdownFence,
  toMdx,
  toMermaidLiveLink,
  toReactComponent,
  type CopyAsContext,
} from './export';
import { toggleInterfaceTheme } from './interfaceTheme';
import { DIAGRAM_THEME_LABELS, DIAGRAM_THEMES, LOOK_LABELS, LOOKS, type DiagramTheme, type Look } from './mermaidRender';
import { toggleVimMode } from './vimMode';

export interface Command {
  id: string;
  label: string;
  category: string;
  /** True only for a command that destroys data — CommandPalette.tsx must confirm inline before calling run(), never execute it straight off a selection. */
  destructive?: boolean;
  run: () => void;
}

// Single source of truth for the confirmation copy — AboutMenu.tsx's own
// confirm card and CommandPalette.tsx's inline confirm (for the same
// destructive: true command) both read this, so the two can't drift.
export const CLEAR_ALL_DATA_CONFIRM_MESSAGE =
  "This deletes your saved draft and diagram history from this device. This can't be undone.";

export interface CommandsContext {
  source: string;
  /** The render loop's current SVG markup — null until the first successful render. Export/Copy commands are omitted entirely while null, matching ExportMenu.tsx/CopyMenu.tsx's own disabled state. */
  svg: string | null;
  diagramType: DiagramType | null;
  onSelectTemplate: (type: DiagramType) => void;
  /** Called with a §9-style confirmation string ("Saved SVG.") after a successful export. */
  onExport: (message: string) => void;
  /** Called with a §9-style confirmation string ("Copied as Markdown.") after a successful copy. */
  onCopy: (message: string) => void;
  onDiagramThemeChange: (diagramTheme: DiagramTheme) => void;
  onLookChange: (look: Look) => void;
  onClearAllData: () => void;
}

// Mirrors CopyMenu.tsx's own COPY_AS_TARGETS list exactly (same seven ids,
// labels, and generators) — kept as a separate literal here rather than
// exported from CopyMenu.tsx, since that file's list is a React component's
// implementation detail, not a shared data structure.
const COPY_AS_TARGETS: { id: string; label: string; generate: (ctx: CopyAsContext) => string }[] = [
  { id: 'markdown', label: 'Markdown', generate: toMarkdownFence },
  { id: 'html-embed', label: 'HTML embed', generate: toHtmlEmbed },
  { id: 'inline-svg', label: 'Inline SVG', generate: toInlineSvg },
  { id: 'data-uri', label: 'Data URI', generate: toDataUri },
  { id: 'react-component', label: 'React component', generate: toReactComponent },
  { id: 'mdx', label: 'MDX/Docusaurus', generate: toMdx },
  { id: 'mermaid-live', label: 'mermaid.live link', generate: toMermaidLiveLink },
];

export function buildCommands(ctx: CommandsContext): Command[] {
  const commands: Command[] = [];

  for (const type of DIAGRAM_TYPES) {
    commands.push({
      id: `new-${type.id}`,
      label: `New: ${type.label}`,
      category: 'New',
      run: () => ctx.onSelectTemplate(type),
    });
  }

  if (ctx.svg !== null) {
    const svg = ctx.svg;
    const filenameBase = ctx.diagramType?.id ?? 'diagram';

    commands.push({
      id: 'export-svg',
      label: 'Export: Save SVG',
      category: 'Export',
      run: () => {
        saveSvg(svg, `${filenameBase}.svg`, false);
        ctx.onExport('Saved SVG.');
      },
    });

    for (const format of RASTER_FORMATS) {
      for (const scale of SCALE_OPTIONS) {
        commands.push({
          id: `export-${format}-${scale}`,
          label: `Export: Save ${RASTER_FORMAT_META[format].label} at ${scale}×`,
          category: 'Export',
          run: () => {
            void saveRaster(svg, `${filenameBase}.${RASTER_FORMAT_META[format].extension}`, {
              format,
              scale,
              transparent: false,
            })
              .then((actualScale) => ctx.onExport(`Saved ${RASTER_FORMAT_META[format].label} at ${actualScale}×.`))
              .catch((err: unknown) => console.error('Raster export failed:', err));
          },
        });
      }
    }

    for (const target of COPY_AS_TARGETS) {
      commands.push({
        id: `copy-${target.id}`,
        label: `Copy as: ${target.label}`,
        category: 'Copy',
        run: () => {
          let text: string;
          try {
            text = target.generate({ source: ctx.source, svg });
          } catch (err) {
            console.error('Copy-as generation failed:', err);
            return;
          }
          navigator.clipboard
            .writeText(text)
            .then(() => ctx.onCopy(`Copied as ${target.label}.`))
            .catch((err: unknown) => console.error('Copy to clipboard failed:', err));
        },
      });
    }
  }

  for (const theme of DIAGRAM_THEMES) {
    commands.push({
      id: `diagram-theme-${theme}`,
      label: `Diagram theme: ${DIAGRAM_THEME_LABELS[theme]}`,
      category: 'Diagram theme',
      run: () => ctx.onDiagramThemeChange(theme),
    });
  }

  for (const look of LOOKS) {
    commands.push({
      id: `look-${look}`,
      label: `Layout: ${LOOK_LABELS[look]}`,
      category: 'Layout',
      run: () => ctx.onLookChange(look),
    });
  }

  commands.push({
    id: 'toggle-interface-theme',
    label: 'Toggle interface theme',
    category: 'Interface',
    run: toggleInterfaceTheme,
  });

  commands.push({
    id: 'toggle-vim-mode',
    label: 'Toggle vim keybindings',
    category: 'Interface',
    run: toggleVimMode,
  });

  commands.push({
    id: 'clear-all-local-data',
    label: 'About: Clear all local data',
    category: 'About',
    destructive: true,
    run: ctx.onClearAllData,
  });

  return commands;
}

/** Case-insensitive substring match against each command's label — an empty query returns every command, unfiltered. */
export function filterCommands(commands: Command[], query: string): Command[] {
  const q = query.trim().toLowerCase();
  if (q === '') return commands;
  return commands.filter((command) => command.label.toLowerCase().includes(q));
}
